// useWorkflowTaskChecklist.js

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Hooks
import useActorCapabilities from 'hooks/useActorCapabilities';
import useNodeContributionHandlers from 'node/hooks/useNodeContributionHandlers';
import useNodeContributionState from 'node/hooks/useNodeContributionState';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// Services
import {
  ensureChecklistForField,
  getChecklist,
  subscribeToChecklistForField,
  updateChecklist,
} from 'services/checklists/ChecklistService';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { subscribeToNodesByKeys } from 'services/nodes/NodeReadService';

// Utilities
import { toggleChecklistItemStatus as toggleChecklistItemsLocally } from 'node/form/fields/checklist/utils/checklistItemUtils';
import {
  buildWorkflowTaskChecklistSeedItems,
  computeWorkflowTaskChecklistProgress,
  isWorkflowTaskChecklistActiveStep,
  readWorkflowTaskChecklistRecipe,
  resolveTaskChecklistFieldName,
} from 'node/missions/mission/utils/workflowStepChecklistUtils';
import { ensureWorkflowStepChecklistResourcesLinked } from 'node/missions/mission/utils/workflowStepChecklistResourceUtils';
import { WORKFLOW_STEP_TASK_TYPE_KEY } from 'node/missions/mission/utils/mintWorkflowStepTaskNodes';
import {
  findMissingRequiredSetFieldAction,
  getChecklistActionButtonLabel,
  getChecklistActionModalFields,
  isChecklistSetFieldActionType,
  isChecklistTaskAttachActionType,
  normalizeChecklistItemActions,
  resolveChecklistMediaContributionKey,
} from 'missions/taskmodal/checklistItemActions';
import {
  collectChecklistResourceNodeKeys,
  normalizeChecklistItemResources,
} from 'utilities/ChecklistItemResources';
import { buildWorkflowChecklistItemChildFields } from 'node/missions/mission/utils/workflowStepAttachmentUtils';
import { canUser, PermissionAction } from 'utilities/NodeSecurity';
import { getResolvedFields } from 'utilities/NodeType';
import {
  getDataSectionName,
  resolveFieldValue,
} from 'views/table/utils/tableFieldUtils';

function flipChecklistItem(checklist, itemKey) {
  if (!checklist || typeof checklist !== 'object') return checklist;

  const currentItems = Array.isArray(checklist?.sourceSnapshot?.items)
    ? checklist.sourceSnapshot.items
    : [];
  const nextItems = toggleChecklistItemsLocally(currentItems, itemKey);

  return {
    ...checklist,
    sourceSnapshot: {
      ...(checklist.sourceSnapshot && typeof checklist.sourceSnapshot === 'object'
        ? checklist.sourceSnapshot
        : {}),
      items: nextItems,
    },
  };
}

/**
 * useWorkflowTaskChecklist
 *
 * Shared Task Checklist controller for a workflow people step: live
 * snapshot, toggle, set-field / attach actions, and complete-at-100%.
 *
 * @param {object} params
 * @param {object|null} [params.task]
 * @param {object|null} [params.stepNode]
 * @param {object|null} [params.hostNode]
 * @param {object|null} [params.template]
 * @param {object|null} [params.user]
 * @param {function} [params.onProgressChange]
 * @param {function} [params.onRequestComplete]
 * @returns {object} Checklist controller.
 */
export default function useWorkflowTaskChecklist({
  task = null,
  stepNode = null,
  hostNode = null,
  template = null,
  user = null,
  onProgressChange = null,
  onRequestComplete = null,
} = {}) {
  const { canEdit } = useActorCapabilities();
  const { openNode } = useNodeOpenIntent();
  const [, setDefaultNode] = useState(null);
  const [, setEditorSaveMode] = useState(null);
  const [, setInitialChildren] = useState([]);
  const [, setInitialActiveNodeKey] = useState(null);
  const [, setIsUploading] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const filesInputRef = useRef(null);
  const pendingChecklistItemKeyRef = useRef('');

  const getChildCreateFields = useCallback(
    () => buildWorkflowChecklistItemChildFields(pendingChecklistItemKeyRef.current),
    [],
  );

  const {
    cameraOpen,
    cameraContentType,
    contributeParent,
    setContributeOpen,
    setContributeParent,
    setPreSelectedType,
    setCameraOpen,
    setCameraContentType,
  } = useNodeContributionState();

  const canAttach = Boolean(canEdit)
    && Boolean(stepNode?.key)
    && canUser(PermissionAction.EDIT, stepNode, user);

  const {
    handleQuickActionSelect,
    handlePhotoSelect,
    handleVideoSelect,
    handleDocumentSelect,
    handleFilesSelect,
    handleCameraContentCreated,
  } = useNodeContributionHandlers({
    node: stepNode,
    user,
    navigate: null,
    setCurrentNode: null,
    setCurrentSection: null,
    contributeParent,
    setContributeOpen,
    setContributeParent,
    setPreSelectedType,
    setCameraOpen,
    setCameraContentType,
    setDefaultNode,
    setEditorSaveMode,
    setInitialChildren,
    setInitialActiveNodeKey,
    setIsUploading,
    photoInputRef,
    videoInputRef,
    documentInputRef,
    filesInputRef,
    getChildCreateFields,
  });

  const templateTask = useMemo(() => {
    const taskKey = String(task?.key || '').trim();
    if (!taskKey || !template) return null;
    return (Array.isArray(template?.tasks) ? template.tasks : [])
      .find((entry) => String(entry?.key || '').trim() === taskKey) || null;
  }, [task?.key, template]);
  const recipe = useMemo(
    () => readWorkflowTaskChecklistRecipe(task, templateTask),
    [task, templateTask],
  );
  const recipeSignature = useMemo(
    () => recipe.items.map((item) => item.key).join('\u0001'),
    [recipe.items],
  );
  const isActive = isWorkflowTaskChecklistActiveStep(task);

  const [checklist, setChecklist] = useState(null);
  const [hasChecklistSnapshot, setHasChecklistSnapshot] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [isAttachCreating, setIsAttachCreating] = useState(false);
  const [resourceNodesByKey, setResourceNodesByKey] = useState(() => new Map());
  const ensureAttemptedRef = useRef('');
  const resourcesLinkedRef = useRef('');
  const wasCompleteRef = useRef(false);
  const dismissCompletePromptRef = useRef(false);
  const checklistRef = useRef(null);
  const pendingWritesRef = useRef(0);
  const persistQueueRef = useRef(Promise.resolve());

  const typeDef = useMemo(
    () => getNodeType(WORKFLOW_STEP_TASK_TYPE_KEY) || null,
    [],
  );
  const fieldName = useMemo(
    () => resolveTaskChecklistFieldName(typeDef),
    [typeDef],
  );

  const hostTypeKey = String(hostNode?.type || '').trim();
  const modalFields = useMemo(() => {
    const schemaFields = hostTypeKey
      ? getResolvedFields(hostTypeKey, { includeHidden: true, excludeSecured: true })
      : [];
    return getChecklistActionModalFields(schemaFields);
  }, [hostTypeKey]);
  const modalFieldsByKey = useMemo(() => {
    const map = new Map();
    modalFields.forEach((field) => {
      map.set(field.key, field);
    });
    return map;
  }, [modalFields]);
  const recipeActionsByKey = useMemo(() => {
    const map = new Map();
    recipe.items.forEach((item) => {
      const key = String(item?.key || '').trim();
      if (!key) return;
      map.set(key, normalizeChecklistItemActions(item?.actions));
    });
    return map;
  }, [recipe.items]);
  const recipeResourcesByKey = useMemo(() => {
    const map = new Map();
    recipe.items.forEach((item) => {
      const key = String(item?.key || '').trim();
      if (!key) return;
      map.set(key, normalizeChecklistItemResources(item?.resources));
    });
    return map;
  }, [recipe.items]);
  const resourceNodeKeys = useMemo(
    () => collectChecklistResourceNodeKeys(recipe.items),
    [recipe.items],
  );

  const baseKey = String(stepNode?.baseKey || '').trim();
  const nodeKey = String(stepNode?.key || '').trim();
  const userKey = String(user?.key || user?.uid || '').trim();

  useEffect(() => {
    checklistRef.current = checklist;
  }, [checklist]);

  useEffect(() => {
    if (!resourceNodeKeys.length) {
      setResourceNodesByKey(new Map());
      return undefined;
    }

    return subscribeToNodesByKeys(resourceNodeKeys, (rows) => {
      const next = new Map();
      (Array.isArray(rows) ? rows : []).forEach((node) => {
        const key = String(node?.key || '').trim();
        if (!key) return;
        next.set(key, node);
      });
      setResourceNodesByKey(next);
    });
  }, [resourceNodeKeys]);

  useEffect(() => {
    const linkSignature = [
      nodeKey,
      resourceNodeKeys.join('\u0001'),
    ].join('|');

    if (!nodeKey || !resourceNodeKeys.length) {
      resourcesLinkedRef.current = '';
      return;
    }
    if (resourcesLinkedRef.current === linkSignature) return;
    resourcesLinkedRef.current = linkSignature;

    ensureWorkflowStepChecklistResourcesLinked({
      recipeItems: recipe.items,
      stepNode,
    }).catch((error) => {
      console.warn('[useWorkflowTaskChecklist] Resource link failed:', error);
    });
  }, [nodeKey, recipe.items, resourceNodeKeys, stepNode]);

  useEffect(() => {
    setChecklist(null);
    checklistRef.current = null;
    setHasChecklistSnapshot(false);
    ensureAttemptedRef.current = '';
    resourcesLinkedRef.current = '';
    wasCompleteRef.current = false;
    dismissCompletePromptRef.current = false;
    pendingWritesRef.current = 0;
    persistQueueRef.current = Promise.resolve();

    if (!baseKey || !nodeKey || !fieldName) {
      return undefined;
    }

    return subscribeToChecklistForField({
      baseKey,
      nodeKey,
      fieldName,
    }, (row) => {
      if (pendingWritesRef.current > 0) {
        setHasChecklistSnapshot(true);
        return;
      }
      setChecklist(row || null);
      checklistRef.current = row || null;
      setHasChecklistSnapshot(true);
    });
  }, [baseKey, fieldName, nodeKey]);

  useEffect(() => {
    if (
      !hasChecklistSnapshot
      || !recipe.items.length
      || !baseKey
      || !nodeKey
      || !userKey
      || !fieldName
    ) {
      return undefined;
    }

    const existingItems = Array.isArray(checklist?.sourceSnapshot?.items)
      ? checklist.sourceSnapshot.items
      : [];
    if (existingItems.length > 0) return undefined;

    const ensureKey = `${nodeKey}:${fieldName}:${recipeSignature}`;
    if (ensureAttemptedRef.current === ensureKey) return undefined;
    ensureAttemptedRef.current = ensureKey;

    let cancelled = false;

    (async () => {
      try {
        await ensureChecklistForField({
          actor: user,
          baseKey,
          fieldLabel: 'Checklist',
          fieldName,
          items: buildWorkflowTaskChecklistSeedItems(recipe.items),
          nodeKey,
          nodeLabel: String(stepNode?.title || task?.title || '').trim(),
          nodeType: WORKFLOW_STEP_TASK_TYPE_KEY,
          userKey,
        });
      } catch (error) {
        if (!cancelled) {
          console.warn('[useWorkflowTaskChecklist] ensure failed:', error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    baseKey,
    checklist,
    fieldName,
    hasChecklistSnapshot,
    nodeKey,
    recipe.items,
    recipeSignature,
    stepNode?.title,
    task?.title,
    user,
    userKey,
  ]);

  const snapshotItems = useMemo(
    () => (
      Array.isArray(checklist?.sourceSnapshot?.items)
        ? checklist.sourceSnapshot.items
        : []
    ),
    [checklist],
  );

  const progress = useMemo(
    () => computeWorkflowTaskChecklistProgress(recipe.items, snapshotItems),
    [recipe.items, snapshotItems],
  );

  useEffect(() => {
    onProgressChange?.(progress);
  }, [onProgressChange, progress]);

  useEffect(() => {
    const isComplete = progress.isComplete === true;
    if (!isComplete) {
      wasCompleteRef.current = false;
      dismissCompletePromptRef.current = false;
      return;
    }

    const crossedToComplete = !wasCompleteRef.current;
    wasCompleteRef.current = true;

    if (
      crossedToComplete
      && isActive
      && progress.totalItems > 0
      && typeof onRequestComplete === 'function'
      && !dismissCompletePromptRef.current
    ) {
      setConfirmOpen(true);
    }
  }, [isActive, onRequestComplete, progress.isComplete, progress.totalItems]);

  const handleOpenSetFieldAction = useCallback((action) => {
    const fieldKey = String(action?.fieldKey || '').trim();
    if (!fieldKey || !hostNode?.key) return;

    const field = modalFieldsByKey.get(fieldKey)
      || modalFields.find((entry) => (
        String(entry?.rawField?.name || '').trim() === fieldKey
        || String(entry?.rawField?.key || '').trim() === fieldKey
      ));

    if (!field?.rawField) return;
    setActiveAction({
      action,
      fieldDef: field.rawField,
    });
  }, [hostNode?.key, modalFields, modalFieldsByKey]);

  const handleToggleItem = useCallback((itemKey) => {
    const normalizedItemKey = String(itemKey || '').trim();
    const checklistKey = String(checklistRef.current?.key || checklist?.key || '').trim();
    if (!checklistKey || !normalizedItemKey) return;

    const previous = checklistRef.current;
    const currentItems = Array.isArray(previous?.sourceSnapshot?.items)
      ? previous.sourceSnapshot.items
      : [];
    const currentItem = currentItems.find((item) => (
      String(item?.key || '').trim() === normalizedItemKey
    ));
    const isCurrentlyDone = String(currentItem?.status || '').toLowerCase() === 'done';

    if (!isCurrentlyDone) {
      const templateKey = String(
        currentItem?.templateKey || normalizedItemKey,
      ).trim();
      const itemActions = recipeActionsByKey.get(templateKey)
        || recipeActionsByKey.get(normalizedItemKey)
        || [];
      const hostDataSection = getDataSectionName(hostTypeKey);
      const missingAction = findMissingRequiredSetFieldAction({
        actions: itemActions,
        getFieldValue: (fieldKey) => {
          const field = modalFieldsByKey.get(fieldKey);
          if (!field?.rawField) return undefined;
          return resolveFieldValue(hostNode, field.rawField, hostDataSection);
        },
      });

      if (missingAction) {
        const actionLabel = getChecklistActionButtonLabel(missingAction, modalFields);
        const label = actionLabel.replace(/^Set\s+/i, '').trim()
          || String(missingAction.fieldKey || 'Field').trim();
        toast.info(`Set ${label} before checking this off`);
        handleOpenSetFieldAction(missingAction);
        return;
      }
    }

    const next = flipChecklistItem(previous, normalizedItemKey);
    if (!next || next === previous) return;

    checklistRef.current = next;
    setChecklist(next);

    pendingWritesRef.current += 1;
    persistQueueRef.current = persistQueueRef.current
      .catch(() => {})
      .then(async () => {
        const latest = checklistRef.current;
        if (!latest?.key) return;

        await updateChecklist(latest.key, {
          title: String(latest.title || '').trim() || 'Checklist',
          sourceSnapshot: {
            templateKey: null,
            templateRevision: null,
            items: Array.isArray(latest?.sourceSnapshot?.items)
              ? latest.sourceSnapshot.items
              : [],
          },
        }, { actor: user });
      })
      .catch(async (error) => {
        console.warn('[useWorkflowTaskChecklist] toggle failed:', error);
        try {
          const serverChecklist = await getChecklist(checklistKey);
          if (serverChecklist && pendingWritesRef.current <= 1) {
            checklistRef.current = serverChecklist;
            setChecklist(serverChecklist);
          }
        } catch {
          // Keep optimistic UI; live subscription will catch up.
        }
      })
      .finally(() => {
        pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      });
  }, [
    checklist?.key,
    handleOpenSetFieldAction,
    hostNode,
    hostTypeKey,
    modalFields,
    modalFieldsByKey,
    recipeActionsByKey,
    user,
  ]);

  const handleConfirmComplete = useCallback(() => {
    setConfirmOpen(false);
    onRequestComplete?.();
  }, [onRequestComplete]);

  const handleDismissComplete = useCallback(() => {
    dismissCompletePromptRef.current = true;
    setConfirmOpen(false);
  }, []);

  const handleOpenTaskAttachAction = useCallback(async (action) => {
    if (!canAttach || isAttachCreating) return;
    const contributionKey = resolveChecklistMediaContributionKey(action);
    if (!contributionKey) return;
    const defaultTitle = String(action?.defaultTitle || '').trim();
    setIsAttachCreating(true);
    try {
      await handleQuickActionSelect(
        contributionKey,
        defaultTitle ? { initialTitle: defaultTitle } : undefined,
      );
    } finally {
      setIsAttachCreating(false);
    }
  }, [canAttach, handleQuickActionSelect, isAttachCreating]);

  const handleOpenAction = useCallback((action, itemKey = '') => {
    pendingChecklistItemKeyRef.current = String(itemKey || '').trim();
    if (isChecklistTaskAttachActionType(action?.type)) {
      handleOpenTaskAttachAction(action);
      return;
    }
    if (isChecklistSetFieldActionType(action?.type)) {
      handleOpenSetFieldAction(action);
    }
  }, [handleOpenSetFieldAction, handleOpenTaskAttachAction]);

  const isActionEnabled = useCallback((action) => {
    if (isChecklistTaskAttachActionType(action?.type)) {
      return canAttach && !isAttachCreating;
    }
    if (isChecklistSetFieldActionType(action?.type)) {
      const fieldKey = String(action?.fieldKey || '').trim();
      return Boolean(hostNode?.key) && modalFieldsByKey.has(fieldKey);
    }
    return false;
  }, [canAttach, hostNode?.key, isAttachCreating, modalFieldsByKey]);

  const handleCloseAction = useCallback(() => {
    setActiveAction(null);
  }, []);

  const handleOpenResource = useCallback((resource) => {
    const resourceKey = String(resource?.nodeKey || '').trim();
    if (!resourceKey) return;
    const liveNode = resourceNodesByKey.get(resourceKey);
    openNode(liveNode || { key: resourceKey }, { preferPeek: true });
  }, [openNode, resourceNodesByKey]);

  const getItemActions = useCallback((item) => {
    const itemKey = String(item?.key || '').trim();
    const templateKey = String(item?.templateKey || itemKey).trim();
    return normalizeChecklistItemActions(
      recipeActionsByKey.get(templateKey)
      || recipeActionsByKey.get(itemKey)
      || [],
    );
  }, [recipeActionsByKey]);

  const getItemResources = useCallback((item) => {
    const itemKey = String(item?.key || '').trim();
    const templateKey = String(item?.templateKey || itemKey).trim();
    return normalizeChecklistItemResources(
      recipeResourcesByKey.get(templateKey)
      || recipeResourcesByKey.get(itemKey)
      || [],
    );
  }, [recipeResourcesByKey]);

  const findSnapshotItem = useCallback((recipeKey) => {
    const key = String(recipeKey || '').trim();
    if (!key) return null;
    return snapshotItems.find((item) => (
      String(item?.templateKey || '').trim() === key
      || String(item?.key || '').trim() === key
    )) || null;
  }, [snapshotItems]);

  return {
    activeAction,
    cameraContentType,
    cameraOpen,
    canAttach,
    confirmOpen,
    documentInputRef,
    fieldName,
    filesInputRef,
    findSnapshotItem,
    getItemActions,
    getItemResources,
    handleCameraContentCreated,
    handleCloseAction,
    handleConfirmComplete,
    handleDismissComplete,
    handleDocumentSelect,
    handleFilesSelect,
    handleOpenAction,
    handleOpenResource,
    handlePhotoSelect,
    handleToggleItem,
    handleVideoSelect,
    hasChecklistSnapshot,
    isActionEnabled,
    isAttachCreating,
    modalFields,
    nodeKey,
    photoInputRef,
    progress,
    recipe,
    recipeActionsByKey,
    recipeResourcesByKey,
    resourceNodesByKey,
    setCameraOpen,
    setConfirmOpen,
    snapshotItems,
    videoInputRef,
  };
}
